import React, { useMemo, useState } from "react";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";
import { appBareUrl } from "../utils/links";

export const MAX_MESSAGE_LENGTH = 300; // matches MAX_MESSAGE_LENGTH in guestPayouts/admin.js
const LEARN_MORE_LINK = appBareUrl("/earn");
const EVENT_LINK_BASE = appBareUrl("/event-details");
// Mirrors INVITE_UTM in guestPayouts/admin.js: only the RSVP link is tagged.
const INVITE_UTM = "utm_source=sms";

/**
 * A coupon the way an operator picks it: what it does and how much is left.
 * Hosts get a plain dot where operators have always had a dash.
 */
export function couponLabel(coupon, { forHost = false } = {}) {
  const what = coupon.discountType === "free"
    ? "free entry"
    : `${coupon.percentageOff}% off`;
  const left = typeof coupon.maxUses === "number"
    ? ` · ${Math.max(0, coupon.maxUses - (coupon.usedCount || 0))} left`
    : "";
  return `${coupon.code}${forHost ? " · " : " — "}${what}${left}`;
}

// A host has no Coupons page (it is operator-only). The main host makes coupons
// in the event's own Coupons section; a co-host has no such section.
function hostNoCoupons(coHost) {
  return coHost
    ? "Only the event's main host can add a coupon here."
    : "None of your coupons work for this event. Add one in this event's Coupons section first.";
}

const firstWord = (name) => (typeof name === "string" ? name.trim().split(/\s+/)[0] : "");

/**
 * The exact message the server will build, mirroring buildInviteCopy in the
 * functions repo's guestPayouts/admin.js. If that copy changes, change it here:
 * an operator sending a text has to see what actually goes out.
 *
 * `noteMode` is how a note joins the text. "replace" (operators) swaps it in
 * for the opener. "append" (hosts) keeps the opener, so an earn text always
 * says "may be eligible", and adds the note as its own paragraph under the
 * host's first name, so the member knows who wrote it.
 */
export function previewMessage({
  name, message, eventName, eventID, coupon, includeEarn, inviteToEvent, noteMode = "replace", senderName,
}) {
  const first = firstWord(name);
  const you = first ? `${first}, you` : "You";
  const event = (eventName || "").trim();
  const opener = includeEarn
    ? `${you}'re one of the most viewed people on Gist. You may be eligible to earn by coming to ${event || "our events"}.`
    : `${you}'re invited to ${event || "an event"} on Gist.`;

  const note = (message || "").trim();
  const parts = noteMode === "append"
    ? [opener, ...(note ? [`A note from ${firstWord(senderName) || "your host"}: ${note}`] : [])]
    : [note || opener];
  if (coupon) {
    parts.push(coupon.discountType === "free"
      ? `Use code ${coupon.code} to get in free.`
      : `Use code ${coupon.code} for ${coupon.percentageOff}% off.`);
  }
  if (inviteToEvent && eventID) parts.push(`RSVP at ${EVENT_LINK_BASE}/${eventID}?${INVITE_UTM}`);
  if (includeEarn) parts.push(`Learn more: ${LEARN_MORE_LINK}`);
  return parts.join("\n\n");
}

/**
 * Reach out to one popular member about one event.
 *
 * ONE message, assembled from the parts the operator ticks: the earn nudge, an
 * invite to this event, a coupon, and an optional note of their own. They all
 * land on the same phone, so sending them as separate texts reads as spam
 * rather than as an invitation — which is why this is one dialog and one send,
 * not the three buttons it replaces.
 *
 * The earn tick does something beyond wording: it marks the member eligible, and
 * eligible is what lets them turn payouts on themselves from /earn. Nobody has
 * to be enrolled before they have been asked.
 *
 * It also decides the channels. An earn message is TEXT ONLY — the programme
 * lives on the web app, and push is APNs-only, so a tapped notification would
 * open an iOS build with no explainer and no payouts card. A plain invite still
 * pushes. A member with texts off therefore cannot be sent an earn nudge at
 * all, and the send says so.
 *
 * `mode="host"` is the same dialog for a host with Popular access, on their own
 * event. The parts are the same; the words are theirs. The earn tick starts
 * off, because for a host it is the extra rather than the point, and their note
 * joins the opener instead of replacing it (see previewMessage). `coupons` must
 * already be the ones a host may send. `coHost` says the host is not the
 * event's main host: coupons stay with the main host, so a co-host gets no
 * coupons and is told why, instead of being sent to a section they do not have.
 * `earnBlocked` is why the server would refuse this host the earn tick for this
 * member (see hostEarnBlockedReason), shown in place of the tick's helper line.
 */
export function GuestOutreachDialog({
  guest, eventName, eventID, coupons, couponsLoading, couponsError, alreadyOnEvent, isSending, onCancel, onConfirm,
  mode = "admin", senderName = "", coHost = false, earnBlocked = null,
}) {
  const forHost = mode === "host";
  const [message, setMessage] = useState("");
  const [earnTicked, setIncludeEarn] = useState(!forHost);
  // Never on while the server would refuse it, whatever the box last said.
  const includeEarn = earnTicked && !earnBlocked;
  const [inviteToEvent, setInviteToEvent] = useState(!alreadyOnEvent && Boolean(eventID));
  const [couponCode, setCouponCode] = useState("");

  const usable = useMemo(
    () => (coupons || []).filter(
      (c) => c.active !== false && (typeof c.maxUses !== "number" || (c.usedCount || 0) < c.maxUses)
    ),
    [coupons]
  );
  const coupon = usable.find((c) => c.code === couponCode) || null;
  const nothingToSay = !includeEarn && !inviteToEvent;

  const preview = previewMessage({
    name: guest?.name, message, eventName, eventID, coupon, includeEarn, inviteToEvent,
    ...(forHost ? { noteMode: "append", senderName } : {}),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-md max-h-[92dvh] overflow-y-auto rounded-t-2xl sm:rounded-lg bg-white dark:bg-gray-900 p-4 sm:p-5 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          {guest?.photoURL ? (
            <img
              src={variantURL(guest.photoURL, 200)}
              onError={fallbackToOriginal(guest.photoURL)}
              alt={guest.name || "Member"}
              className="w-12 h-12 rounded-full object-cover shrink-0"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
              <span className="text-gray-500 dark:text-gray-400">{(guest?.name || "?").charAt(0).toUpperCase()}</span>
            </div>
          )}
          <div className="min-w-0">
            <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {guest?.name || "Unnamed member"}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {includeEarn ? "One text" : "One text and one push"} about {eventName || "this event"}
            </div>
          </div>
        </div>

        <fieldset className="flex flex-col gap-2">
          <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={includeEarn}
              onChange={(e) => setIncludeEarn(e.target.checked)}
              disabled={isSending || Boolean(earnBlocked)}
              className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
            />
            {forHost ? (
              <span className={earnBlocked ? "text-gray-400 dark:text-gray-500" : undefined}>
                Tell them they may be able to earn by coming
                {earnBlocked ? (
                  <span className="block text-xs text-amber-700 dark:text-amber-400">{earnBlocked}</span>
                ) : (
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Gist pays popular guests a share of what the night makes. It never comes out of your payout.
                  </span>
                )}
              </span>
            ) : (
              <span>
                Tell them they can earn at this event
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Adds the explainer link, and lets them turn payouts on themselves.
                </span>
              </span>
            )}
          </label>

          <label className={`flex items-start gap-2 text-sm ${alreadyOnEvent || !eventID ? "text-gray-400 dark:text-gray-500" : "text-gray-700 dark:text-gray-200"}`}>
            <input
              type="checkbox"
              checked={inviteToEvent}
              onChange={(e) => setInviteToEvent(e.target.checked)}
              disabled={isSending || alreadyOnEvent || !eventID}
              className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
            />
            <span>
              Invite them to this event
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {alreadyOnEvent
                  ? "They are already on this event."
                  : "Adds them to the invite list and includes the RSVP link."}
              </span>
            </span>
          </label>
        </fieldset>

        <label htmlFor="outreach-coupon" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mt-4 mb-1">
          Coupon (optional)
        </label>
        <select
          id="outreach-coupon"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          disabled={isSending || couponsLoading || usable.length === 0}
          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        >
          <option value="">No coupon</option>
          {usable.map((c) => (
            <option key={c.id} value={c.code}>{couponLabel(c, { forHost })}</option>
          ))}
        </select>
        {!couponsLoading && usable.length === 0 && (
          <p className={`mt-1 text-xs ${couponsError ? "text-amber-700 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"}`}>
            {couponsError || (forHost ? hostNoCoupons(coHost)
              : "No coupon this event can use. Make one on the Coupons page first.")}
          </p>
        )}

        <label htmlFor="outreach-message" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mt-4 mb-1">
          {forHost ? "Add a note (sent with your first name)" : "Message (optional)"}
        </label>
        <textarea
          id="outreach-message"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          disabled={isSending}
          placeholder={forHost ? "Optional. Why you'd like them there." : "Leave blank to send the standard note."}
          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
        <div className="flex justify-between items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
          <span>
            {forHost
              ? "It goes under the first line. The links are added for you."
              : "Your note replaces the opener. The links are added for you."}
          </span>
          <span>{message.length}/{MAX_MESSAGE_LENGTH}</span>
        </div>

        <p className="mt-3 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">They will get:</p>
        <p className="p-3 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line break-words">
          {preview}
        </p>

        {nothingToSay && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            Tick earning, the event invite, or both. There is nothing to send otherwise.
          </p>
        )}

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={() => onConfirm({
              message: message.trim(),
              includeEarn,
              inviteToEvent,
              couponCode: coupon ? coupon.code : "",
            })}
            disabled={isSending || nothingToSay}
            className="flex-1 sm:flex-none px-4 py-2 rounded-md font-medium bg-primary text-white hover:bg-primaryDark transition-colors disabled:opacity-50"
          >
            {isSending ? "Sending..." : "Send"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSending}
            className="flex-1 sm:flex-none px-4 py-2 rounded-md font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default GuestOutreachDialog;
